/**
 * Email notification subscriptions + recipients (migration 20270328000000).
 *
 * One dataset, two views: the podiatrist settings tab (own rows, RLS
 * self-scoped) and the admin matrix on the Email templates page (all rows,
 * RLS admin-scoped). DEFAULT OFF is structural: absence of a subscription row
 * means off, and every DB sender joins on `enabled`.
 *
 * Recipients: the login address is the implicit primary; every other address
 * is created by the add_email_recipient RPC (which owns the verification
 * token and sends the handshake email) and receives NOTHING until verified.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export interface EmailNotificationKind {
  kind: string;
  template_key: string;
  title: string;
  description: string;
  cadence_label: string;
  sort_order: number;
}

export interface EmailSubscriptionRow {
  user_id: string;
  kind: string;
  enabled: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface EmailRecipientRow {
  id: string;
  user_id: string;
  kind: string;
  address: string;
  is_primary: boolean;
  verified_at: string | null;
  last_verification_sent_at: string | null;
  added_at: string;
}

export const emailNotificationKeys = {
  all: ['email-notifications'] as const,
  kinds: () => [...emailNotificationKeys.all, 'kinds'] as const,
  mine: (userId: string) => [...emailNotificationKeys.all, 'mine', userId] as const,
  adminMatrix: () => [...emailNotificationKeys.all, 'admin-matrix'] as const,
};

/* eslint-disable @typescript-eslint/no-explicit-any -- new tables/RPCs land in
   generated types on the next gen-types run; boundary casts until then. */

export function emailNotificationKindsQuery(client: SupabaseClient<Database>) {
  return {
    queryKey: emailNotificationKeys.kinds(),
    queryFn: async (): Promise<EmailNotificationKind[]> => {
      const { data, error } = await (client.from as any)('email_notification_kinds')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as EmailNotificationKind[];
    },
  };
}

/** The caller's own subscriptions + recipients, for the settings tab. */
export function myEmailNotificationsQuery(client: SupabaseClient<Database>, userId: string) {
  return {
    queryKey: emailNotificationKeys.mine(userId),
    queryFn: async (): Promise<{
      subscriptions: EmailSubscriptionRow[];
      recipients: EmailRecipientRow[];
    }> => {
      const [subs, recips] = await Promise.all([
        (client.from as any)('email_notification_subscriptions').select('*').eq('user_id', userId),
        (client.from as any)('email_notification_recipients')
          .select('id, user_id, kind, address, is_primary, verified_at, last_verification_sent_at, added_at')
          .eq('user_id', userId)
          .order('added_at'),
      ]);
      if (subs.error) throw subs.error;
      if (recips.error) throw recips.error;
      return {
        subscriptions: (subs.data ?? []) as EmailSubscriptionRow[],
        recipients: (recips.data ?? []) as EmailRecipientRow[],
      };
    },
  };
}

/** Everything, for the admin matrix (RLS admits admins only). */
export function adminEmailNotificationMatrixQuery(client: SupabaseClient<Database>) {
  return {
    queryKey: emailNotificationKeys.adminMatrix(),
    queryFn: async (): Promise<{
      subscriptions: EmailSubscriptionRow[];
      recipients: EmailRecipientRow[];
    }> => {
      const [subs, recips] = await Promise.all([
        (client.from as any)('email_notification_subscriptions').select('*'),
        (client.from as any)('email_notification_recipients')
          .select('id, user_id, kind, address, is_primary, verified_at, last_verification_sent_at, added_at')
          .order('added_at'),
      ]);
      if (subs.error) throw subs.error;
      if (recips.error) throw recips.error;
      return {
        subscriptions: (subs.data ?? []) as EmailSubscriptionRow[],
        recipients: (recips.data ?? []) as EmailRecipientRow[],
      };
    },
  };
}

/**
 * Flip a subscription. Works for self (settings tab) and for any user when
 * the caller is an admin (matrix) - RLS decides; the DB trigger stamps
 * updated_by = the actual actor either way.
 */
export async function setEmailSubscription(
  client: SupabaseClient<Database>,
  args: { userId: string; kind: string; enabled: boolean },
): Promise<void> {
  const { error } = await (client.from as any)('email_notification_subscriptions').upsert(
    { user_id: args.userId, kind: args.kind, enabled: args.enabled },
    { onConflict: 'user_id,kind' },
  );
  if (error) throw error;
}

/** Add a recipient address (RPC generates the token + sends the handshake). */
export async function addEmailRecipient(
  client: SupabaseClient<Database>,
  args: { kind: string; address: string; isPrimary?: boolean },
): Promise<{ id: string; verified: boolean; verification_posted?: boolean }> {
  const { data, error } = await (client.rpc as any)('add_email_recipient', {
    p_kind: args.kind,
    p_address: args.address,
    p_is_primary: args.isPrimary ?? false,
  });
  if (error) throw error;
  return data as { id: string; verified: boolean; verification_posted?: boolean };
}

export async function resendRecipientVerification(
  client: SupabaseClient<Database>,
  recipientId: string,
): Promise<boolean> {
  const { data, error } = await (client.rpc as any)('resend_recipient_verification', {
    p_recipient_id: recipientId,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function deleteEmailRecipient(
  client: SupabaseClient<Database>,
  recipientId: string,
): Promise<void> {
  const { error } = await (client.from as any)('email_notification_recipients')
    .delete()
    .eq('id', recipientId);
  if (error) throw error;
}

/**
 * Read-only rendered preview of a template (server-side sample variables;
 * any authenticated user; nothing is sent or logged).
 */
export async function fetchEmailTemplatePreview(
  client: SupabaseClient<Database>,
  templateKey: string,
): Promise<{ subject: string; html: string }> {
  const { data, error } = await client.functions.invoke<{
    data: { subject: string; html: string } | null;
    error: { code: string; message: string } | null;
  }>('send-email', { body: { preview: true, template_key: templateKey } });
  if (error) throw new Error('Could not load the template preview.');
  if (!data?.data) throw new Error(data?.error?.message ?? 'Empty preview response.');
  return data.data;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-explicit-any -- new RPC lands in the
   generated types on the next gen-types run. */

/**
 * What a podiatrist has already been told about an order's delivery date
 * (migration 20270330000000). The Order Tracker asks this BEFORE committing a
 * date change, so the admin learns at the moment of the edit that a
 * podiatrist was already promised a date and will not be corrected
 * automatically.
 *
 *   never_announced - nobody has been told anything; edit freely.
 *   window_open     - announced TODAY and it is before 21:00 Dubai, so
 *                     tonight's correction email will carry the new date.
 *   window_closed   - announced, and that announcement's 21:00 correction
 *                     window has passed. NO automatic email will tell them.
 */
export interface DeliveryAnnouncementDetail {
  announced_on: string;
  announced_at: string;
  promised_edd: string | null;
  corrected_already: boolean;
  podiatrist_name: string | null;
  podiatrist_email: string | null;
}

// Three DISTINCT members, so Extract<..., {state:'window_closed'}> narrows in
// consumers (a single member with a union-typed field yields never).
export type DeliveryAnnouncementState =
  | { state: 'never_announced' }
  | ({ state: 'window_open' } & DeliveryAnnouncementDetail)
  | ({ state: 'window_closed' } & DeliveryAnnouncementDetail);

export async function fetchDeliveryAnnouncementState(
  client: SupabaseClient<Database>,
  orderId: string,
): Promise<DeliveryAnnouncementState> {
  const { data, error } = await (client.rpc as any)('fn_delivery_announcement_state', {
    p_order_id: orderId,
  });
  if (error) throw error;
  return data as DeliveryAnnouncementState;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
